import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, RefreshCw, Unplug } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { toast } from 'sonner';

import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FieldContent, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { PageContainer } from '../../../common/components/PageContainer';
import { AdConnection, AdProvider, useAdsApi } from '../../../common/hooks/use-ads-api.hook';
import { FeatureGate } from '../../../components/FeatureGate';
import { ConnectionStatusBadge, PageHeading, ProviderMark, readApiError, useAdConnections } from './ads-shared';

dayjs.extend(relativeTime);

const ConnectionsBody = () => {
    const { t } = useTranslation();
    const api = useAdsApi();
    const connections = useAdConnections();
    const [params, setParams] = useSearchParams();
    const [starting, setStarting] = useState<AdProvider | null>(null);
    const [syncing, setSyncing] = useState<string | null>(null);
    const [pending, setPending] = useState<AdConnection[]>([]);
    const [chosen, setChosen] = useState<string[]>([]);
    const [choosing, setChoosing] = useState(false);
    const [disconnecting, setDisconnecting] = useState<AdConnection | null>(null);

    /*
     * Coming back from the platform's consent screen.
     *
     * The API has parked every account the token can reach; the vendor now
     * picks which ones are theirs. A login that reaches an agency's other
     * clients must not put someone else's spending on this dashboard.
     */
    useEffect(() => {
        const result = params.get('connected');
        if (!result) return;

        if (result === 'cancelled') {
            toast.message(t('ADS.CONNECTIONS.CANCELLED', 'Connection cancelled.'));
        } else if (result === 'failed') {
            toast.error(params.get('reason') || t('ADS.CONNECTIONS.FAILED', 'The account could not be connected.'));
        } else {
            api.getPendingConnections()
                .then((rows) => {
                    const fresh = rows.filter((r) => r.provider === result);
                    if (!fresh.length) {
                        toast.error(t('ADS.CONNECTIONS.NO_ACCOUNTS', 'That login has no ad accounts we can use.'));
                        return;
                    }
                    setPending(fresh);
                    setChosen(fresh.length === 1 ? [fresh[0].uuid] : []);
                })
                .catch(() => undefined);
        }

        // Clean the address so a refresh does not replay it.
        params.delete('connected');
        params.delete('reason');
        params.delete('accounts');
        setParams(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const connect = async (provider: AdProvider) => {
        setStarting(provider);
        try {
            const { url } = await api.authorize(provider);
            window.location.href = url;
        } catch (error) {
            toast.error(await readApiError(error, t('ADS.CONNECTIONS.START_FAILED', 'The connection could not be started.')));
            setStarting(null);
        }
    };

    const confirmChoice = async () => {
        setChoosing(true);
        try {
            await api.selectConnections(chosen);
            setPending([]);
            toast.success(t('ADS.CONNECTIONS.CONNECTED_TOAST', 'Connected. Pulling your campaigns now…'));
            await connections.reload();
            await api.sync().catch(() => undefined);
            await api.refreshSuggestions().catch(() => undefined);
            await connections.reload();
        } catch (error) {
            toast.error(await readApiError(error, t('ADS.CONNECTIONS.FAILED', 'The account could not be connected.')));
        } finally {
            setChoosing(false);
        }
    };

    const syncOne = async (connection: AdConnection) => {
        setSyncing(connection.uuid);
        try {
            const result = await api.sync(connection.uuid);
            if (result.ok === false) toast.error(result.error ?? t('ADS.SYNC.FAILED', 'The accounts could not be synced.'));
            else toast.success(t('ADS.CONNECTIONS.SYNCED', '{{name}}: {{count}} campaigns updated.', { name: connection.accountName, count: result.campaigns ?? 0 }));
            connections.reload();
        } catch (error) {
            toast.error(await readApiError(error, t('ADS.SYNC.FAILED', 'The accounts could not be synced.')));
        } finally {
            setSyncing(null);
        }
    };

    const disconnect = async () => {
        if (!disconnecting) return;
        try {
            await api.disconnect(disconnecting.uuid);
            toast.success(t('ADS.CONNECTIONS.DISCONNECTED_TOAST', '{{name}} disconnected.', { name: disconnecting.accountName }));
            setDisconnecting(null);
            connections.reload();
        } catch (error) {
            toast.error(await readApiError(error, t('ADS.CONNECTIONS.DISCONNECT_FAILED', 'It could not be disconnected.')));
        }
    };

    const providerText: Record<AdProvider, { name: string; description: string }> = {
        meta: {
            name: 'Meta Ads',
            description: t('ADS.CONNECTIONS.META_DESC', 'Facebook and Instagram campaigns. You grant read and manage access to your ad accounts; you can revoke it from Meta at any time.'),
        },
        google: {
            name: 'Google Ads',
            description: t('ADS.CONNECTIONS.GOOGLE_DESC', 'Search, Performance Max and YouTube campaigns, including accounts your agency manages for you.'),
        },
    };

    const list = connections.data?.connections ?? [];
    const providers = connections.data?.providers ?? [{ provider: 'meta', configured: false }, { provider: 'google', configured: false }];

    return (
        <div className="flex flex-col gap-6">
            <PageHeading
                title={t('ADS.CONNECTIONS.TITLE', 'Ad accounts')}
                subtitle={t('ADS.CONNECTIONS.SUBTITLE', 'Connect the accounts your ads run from. We read campaigns and daily figures, and change them only when you ask.')}
            />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {providers.map(({ provider, configured }) => {
                    const count = list.filter((c) => c.provider === provider).length;
                    return (
                        <Card key={provider}>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <ProviderMark provider={provider} />
                                    {providerText[provider as AdProvider].name}
                                </CardTitle>
                                <CardDescription>{providerText[provider as AdProvider].description}</CardDescription>
                                {count > 0 && (
                                    <CardAction>
                                        <span className="text-sm text-muted-foreground">
                                            {t('ADS.CONNECTIONS.COUNT', '{{count}} connected', { count })}
                                        </span>
                                    </CardAction>
                                )}
                            </CardHeader>
                            <CardFooter className="justify-between gap-2">
                                <span className="text-xs text-muted-foreground">
                                    {configured ? '' : t('ADS.CONNECTIONS.NOT_CONFIGURED', 'Not available yet — contact us to enable it.')}
                                </span>
                                <Button variant="outline" size="sm" onClick={() => connect(provider as AdProvider)} disabled={!configured || starting !== null}>
                                    {starting === provider ? <Spinner data-icon="inline-start" /> : <ExternalLink data-icon="inline-start" />}
                                    {count ? t('ADS.CONNECTIONS.ADD_ANOTHER', 'Add an account') : t('ADS.CONNECTIONS.CONNECT', 'Connect')}
                                </Button>
                            </CardFooter>
                        </Card>
                    );
                })}
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>{t('ADS.CONNECTIONS.CONNECTED_TITLE', 'Connected accounts')}</CardTitle>
                    <CardDescription>{t('ADS.CONNECTIONS.CONNECTED_DESC', 'Figures refresh four times a day. Sync one now if you have just changed something in the platform.')}</CardDescription>
                </CardHeader>
                <CardContent>
                    {connections.loading ? <Skeleton className="h-24 w-full" /> : list.length ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('ADS.CONNECTIONS.ACCOUNT', 'Account')}</TableHead>
                                    <TableHead>{t('ADS.CONNECTIONS.STATUS', 'Status')}</TableHead>
                                    <TableHead>{t('ADS.CONNECTIONS.LAST_SYNC', 'Last sync')}</TableHead>
                                    <TableHead className="w-48"><span className="sr-only">{t('ADS.COMMON.ACTIONS', 'Actions')}</span></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {list.map((c) => (
                                    <TableRow key={c.uuid}>
                                        <TableCell>
                                            <span className="flex items-center gap-2">
                                                <ProviderMark provider={c.provider} />
                                                <span className="flex flex-col">
                                                    <span>{c.accountName}</span>
                                                    <span className="text-xs text-muted-foreground">
                                                        {c.externalAccountId}{c.currency ? ` · ${c.currency}` : ''}
                                                    </span>
                                                </span>
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <span className="flex flex-col items-start gap-1">
                                                <ConnectionStatusBadge status={c.status} />
                                                {c.lastError && <span className="max-w-72 text-xs text-destructive">{c.lastError}</span>}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {c.lastSyncAt ? dayjs(c.lastSyncAt).fromNow() : t('ADS.CONNECTIONS.NEVER', 'Never')}
                                        </TableCell>
                                        <TableCell>
                                            <span className="flex justify-end gap-2">
                                                {c.status === 'needs_reauth' ? (
                                                    <Button variant="outline" size="sm" onClick={() => connect(c.provider)}>
                                                        {t('ADS.OVERVIEW.REAUTH_CTA', 'Reconnect')}
                                                    </Button>
                                                ) : (
                                                    <Button variant="outline" size="sm" onClick={() => syncOne(c)} disabled={syncing === c.uuid}>
                                                        {syncing === c.uuid ? <Spinner data-icon="inline-start" /> : <RefreshCw data-icon="inline-start" />}
                                                        {t('ADS.SYNC.BUTTON', 'Sync now')}
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="outline"
                                                    size="icon-sm"
                                                    className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                                    aria-label={t('ADS.CONNECTIONS.DISCONNECT', 'Disconnect')}
                                                    onClick={() => setDisconnecting(c)}
                                                >
                                                    <Unplug />
                                                </Button>
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <p className="py-6 text-center text-sm text-muted-foreground">
                            {t('ADS.CONNECTIONS.NONE', 'No ad account connected yet.')}
                        </p>
                    )}
                </CardContent>
            </Card>

            <Dialog open={pending.length > 0} onOpenChange={(o) => !o && !choosing && setPending([])}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('ADS.CONNECTIONS.CHOOSE_TITLE', 'Which accounts are yours?')}</DialogTitle>
                        <DialogDescription>
                            {t('ADS.CONNECTIONS.CHOOSE_BODY', 'Your login can reach these ad accounts. Choose the ones this shop advertises from.')}
                        </DialogDescription>
                    </DialogHeader>
                    <FieldGroup>
                        {pending.map((account) => (
                            <Field key={account.uuid} orientation="horizontal">
                                <Checkbox
                                    id={`acct-${account.uuid}`}
                                    checked={chosen.includes(account.uuid)}
                                    onCheckedChange={(checked) =>
                                        setChosen((current) => checked ? [...current, account.uuid] : current.filter((u) => u !== account.uuid))}
                                />
                                <FieldContent>
                                    <FieldLabel htmlFor={`acct-${account.uuid}`}>{account.accountName}</FieldLabel>
                                    <FieldDescription>{account.externalAccountId}{account.currency ? ` · ${account.currency}` : ''}</FieldDescription>
                                </FieldContent>
                            </Field>
                        ))}
                    </FieldGroup>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPending([])} disabled={choosing}>{t('ADS.COMMON.CANCEL', 'Cancel')}</Button>
                        <Button onClick={confirmChoice} disabled={!chosen.length || choosing}>
                            {choosing && <Spinner data-icon="inline-start" />}
                            {t('ADS.CONNECTIONS.CONNECT_CHOSEN', 'Connect {{count}}', { count: chosen.length })}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!disconnecting} onOpenChange={(o) => !o && setDisconnecting(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('ADS.CONNECTIONS.DISCONNECT_TITLE', 'Disconnect {{name}}?', { name: disconnecting?.accountName })}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('ADS.CONNECTIONS.DISCONNECT_BODY', 'We stop reading it and forget the access you granted. Your campaigns keep running in the platform, and the figures already pulled stay here.')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('ADS.COMMON.CANCEL', 'Cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            className="border border-destructive bg-transparent text-destructive hover:bg-destructive/10"
                            onClick={(e) => { e.preventDefault(); disconnect(); }}
                        >
                            {t('ADS.CONNECTIONS.DISCONNECT', 'Disconnect')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export const AdsConnectionsPage = () => {
    const { t } = useTranslation();
    return (
        <PageContainer breadcrumbs={[{ label: t('SIDEBAR.ADS.TITLE', 'Ads') }, { label: t('SIDEBAR.ADS.CONNECTIONS', 'Ad accounts') }]}>
            <FeatureGate feature="adsManager">
                <ConnectionsBody />
            </FeatureGate>
        </PageContainer>
    );
};

export default AdsConnectionsPage;
